import React, { useContext, useEffect, useReducer, useState } from 'react'
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';
import debounce from "lodash.debounce";
import { dataReducer } from './dataReducers'
import { refreshSession, useGetCurrentSession } from '../../services/api-query/auth'
import { useGetDashboardData } from '../../services/api-query/dashboardData'
import { useGetCustomer, welcomeBanner } from '../../services/api-query/customer'
import { 
  useGetAnualSavings 
} from '../../services/api-query/savingsData'
import { 
  useGetSystemInfo, 
  setHotwaterSavingMode 
} from '../../services/api-query/systemInfo'
import { updateDirectDebitBank } from '../../services/api-query/directDebit'
import { submitFeedback } from '../../services/api-query/feedback'
import { askHelp } from '../../services/api-query/help'
import { AuthContext } from '../auth/authContext'
import defaultState from './defaultState'

const initialState = defaultState()

type Props = {
    children?: React.ReactNode
  }

export const DataContext = React.createContext(initialState)
export const DataDispatchContext = React.createContext(null)



const DataProvider = ({ children }: Props) => {
  const auth = useContext(AuthContext);
  const [displayWelcome, setDisplayWelcome] = useState((welcomeBanner()))
  const [ data, dispatch] = useReducer(dataReducer, initialState );
  const { 
    errorSession, 
    currentSession, 
    refetchSession 
  } = useGetCurrentSession();
  const { customer } = useGetCustomer(currentSession);
  const { errorSysInfo, system } = useGetSystemInfo(currentSession);
  const { 
    annualSavingsReports
  } = useGetAnualSavings(currentSession)
  const { 
    refetch, 
    isFetching,
    dashboardPending, 
    errorDashboardData, 
    dashboardData 
  } = useGetDashboardData(currentSession)
  const invalidSession = 'Invalid user session' 
  if(
    errorSession ||
    errorSysInfo?.message === invalidSession ||
    errorDashboardData?.message === invalidSession 
  ) {
    auth.signOut();
  }

  const submitFeedbackData = async (feedback: any) => {
    try {
      await submitFeedback({
        id: feedback?.id,
        selectedOptionsIds: feedback?.selectedTopic?[feedback?.selectedTopic]:[],
        isAllowedToContact: feedback?.allowContact,
        feedbackText: `${auth?.staff?`[impersonated by: ${auth?.staff?.split('::')[1]}]  `:''}${feedback?.description}` 
      }, currentSession)
    } catch(e: any) {
      console.log(e)
      return 'Error in submitting feedback, please try again.'
    }
    return 'submitted'
  }

  const setSavingMode = async (mode: string) => {
    try {
      await setHotwaterSavingMode(mode, currentSession)
    } catch {
      return 'Error in setting hotwater saving mode, please try again.'
    }
    return 'saved'
  }
  
  const askAIhelp = async (question: string) => {
    try {
      const { reply } = 
              await askHelp(data?.chat?.threadId, question, currentSession)
      if(reply) {
        return dispatch({
          key: uuidv4(),
          type: 'newAiConversation',
          message: reply,
          from: 'sunny',
          chatWindow: data?.chat.chatWindow + 
                      ((reply.length / 100).toFixed(0) + 5)
       })
      }
    } catch {
      console.log('AI assistant is currently offline')
    }
    return dispatch({
      key: uuidv4(),
      type: 'newAiConversation',
      message: 'AI assistant is currently offline',
      from: 'system',
      chatWindow: data?.chat.chatWindow + 5
   })

  }

  const hideWelcome = ()=>{
    welcomeBanner(true)
    setDisplayWelcome(false)
  }

  const updateDirectDebit = async () => {
    let errMessage: any = null;
    try {
      const parts = `${data?.directDebit?.accountNumber?.trim()}`.split('-')
      if( data?.directDebit?.accountName &&
          parts.length===4 && 
          parts.every(p=>p)) {
        const bank = {
          number: parts[0],
          branch: parts[1],
          account: parts[2],
          suffix: parts[3],
          accountName: data?.directDebit?.accountName
        }
        await updateDirectDebitBank(currentSession, bank)
      } else {
        errMessage = 'Bank account is invalid'
      }
    } catch(err: any) {
      errMessage = err?.message
    }
    if(errMessage) {
      dispatch({
        type: 'setDirectDebitBank',
        directDebitBank: { 
          ...data?.directDebit,
          error: errMessage
        }
      })
    }  
  }

  useEffect(() => {
    if(dashboardData) {
      dispatch({
        type: 'setDashboardData',
        dashboardData,
     })
    }
    if(annualSavingsReports) {
      dispatch({
        type: 'setSavingsData',
        annualSavingsReports,
     })
    }
  },[dashboardData, annualSavingsReports, dispatch])

  const refresh = () =>{
    refetchSession()
    .then(()=>refetch())
  }

  const prepareCsvData = (monthData: any) => {
    const csvData: any[] = []
    if(monthData){
      const days = moment.tz(moment(monthData.startDate),monthData.timezone).daysInMonth()
      const month = monthData.tip?.trim()?.split(' ')[0]?.substr(0,3);
      (monthData.solar?.series || []).forEach((s:number,i:number)=>{
        if(days>=i+1) {
          const load = Math.abs(monthData.grid?.series?.import[i]) + 
                      monthData.solar?.series[i] -
                      Math.abs(monthData.grid?.series?.export[i])
          csvData.push({
            month,
            day: i+1,
            solar: monthData.solar?.series[i],
            charge: Math.abs(monthData.battery?.series?.charge[i]),
            discharge: Math.abs(monthData.battery?.series?.discharge[i]),
            export: Math.abs(monthData.grid?.series?.export[i]),
            import: Math.abs(monthData.grid?.series?.import[i]),
            load: parseFloat(load.toFixed(2)),
            ev: ''
          })
        }
      })
    }
    return csvData;
  }


  return (
    <DataContext.Provider value={{ 
          ...data,
          system, 
          customer,
          askAIhelp,
          setSavingMode,
          submitFeedbackData,
          displayWelcome,
          hideWelcome,
          updateDirectDebit,
          prepareCsvData,
          refetchDashboard: debounce(refresh, 500, { trailing: true }),
         }}>
      <DataDispatchContext.Provider value={dispatch as any}>
        {children}
      </DataDispatchContext.Provider>
    </DataContext.Provider>
  );
}

export default DataProvider
